import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/SystemDetailPage.css';

interface SystemInfo {
  id: string;
  name: string;
  status: string;
  icon: string;
  issues: string[];
  riskLevel: string;
}

interface SystemDetailPageProps {
  onBack?: () => void;
}

const SystemDetailPage: React.FC<SystemDetailPageProps> = ({ onBack }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();

  const healthSystems: SystemInfo[] = [
    {
      id: 'digestive',
      name: '消化系统',
      status: 'warning',
      icon: '🍽️',
      issues: [
        '幽门螺旋杆菌阳性|2023年3月|胃部感染，经治疗后已好转，建议定期复查', 
        '脂肪肝|2023年6月|轻度脂肪肝，与饮食和生活习惯相关，需控制饮食', 
        '胃炎轻度|2022年12月|慢性浅表性胃炎，症状轻微，注意饮食规律', 
        '消化不良|2023年1月|功能性消化不良，通过调整饮食已改善', 
        '肠易激综合征|2022年8月|压力相关性肠道功能紊乱，需心理调节', 
        '胆囊息肉|2023年4月|小型胆囊息肉，无症状，定期超声监测即可'
      ],
      riskLevel: '重点监测'
    },
    {
      id: 'respiratory',
      name: '呼吸系统',
      status: 'normal',
      icon: '🫁',
      issues: [],
      riskLevel: '维护保持'
    },
    {
      id: 'nervous',
      name: '神经系统',
      status: 'normal',
      icon: '🧠',
      issues: [],
      riskLevel: '维护保持'
    },
    {
      id: 'circulatory',
      name: '循环系统',
      status: 'caution',
      icon: '❤️',
      issues: ['高血压风险提醒|2023年7月|血压偏高趋势，与生活压力和饮食习惯相关，需监测'],
      riskLevel: '常规预警'
    },
    {
      id: 'endocrine',
      name: '内分泌系统',
      status: 'caution',
      icon: '⚡',
      issues: ['2型糖尿病风险预警|2023年5月|血糖略高，与家族史和体重相关，需控制饮食'],
      riskLevel: '常规预警'
    },
    {
      id: 'motor',
      name: '运动系统',
      status: 'normal',
      icon: '🦴',
      issues: [],
      riskLevel: '维护保持'
    },
    {
      id: 'urinary',
      name: '泌尿系统',
      status: 'normal',
      icon: '🫘',
      issues: [],
      riskLevel: '维护保持'
    },
    {
      id: 'reproductive',
      name: '生殖系统',
      status: 'normal',
      icon: '🌸',
      issues: [],
      riskLevel: '维护保持'
    },
    {
      id: 'immune',
      name: '免疫系统',
      status: 'normal',
      icon: '🛡️',
      issues: [],
      riskLevel: '维护保持'
    }
  ];

  const system = healthSystems.find(s => s.id === systemId);

  if (!system) {
    return (
      <div className="system-detail-page">
        <div className="ios-nav-bar">
          <button className="ios-back-button" onClick={() => navigate('/chat')}>
            <span className="ios-back-icon">‹</span>
            返回
          </button>
          <div className="ios-nav-title">系统详情</div>
          <div className="ios-nav-spacer"></div>
        </div>
        <div className="ios-error-view">
          <div className="ios-error-icon">⚠️</div>
          <div className="ios-error-text">系统未找到</div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return '#34c759';
      case 'caution': return '#ff9500';
      case 'warning': return '#ff3b30';
      case 'danger': return '#ff3b30';
      default: return '#34c759';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'normal': return '继续保持';
      case 'caution': return '常规筛查';
      case 'warning': return '重点检查';
      case 'danger': return '立即就医';
      default: return '继续保持';
    }
  };

  const getCheckPlan = (systemId: string) => {
    const plans: Record<string, Array<{type: string, frequency: string, description: string, nextDate: string}>> = {
      'digestive': [
        { type: '胃镜检查', frequency: '每年1次', description: '监测胃炎恢复情况，筛查胃癌风险', nextDate: '2025年8月' },
        { type: '腹部超声', frequency: '每半年1次', description: '观察脂肪肝变化和胆囊息肉情况', nextDate: '2025年9月' },
        { type: '幽门螺旋杆菌检测', frequency: '每2年1次', description: '确认根除治疗效果', nextDate: '2026年3月' }
      ],
      'circulatory': [
        { type: '血压监测', frequency: '每周2次', description: '家庭血压监测，记录血压变化', nextDate: '持续进行' },
        { type: '心电图检查', frequency: '每半年1次', description: '评估心脏功能，排查心律不齐', nextDate: '2025年11月' },
        { type: '血脂检查', frequency: '每3个月1次', description: '监测血脂水平变化', nextDate: '2025年10月' }
      ],
      'endocrine': [
        { type: '血糖监测', frequency: '每月1次', description: '空腹血糖和餐后血糖检测', nextDate: '2025年8月' },
        { type: '糖化血红蛋白', frequency: '每3个月1次', description: '评估血糖长期控制情况', nextDate: '2025年10月' },
        { type: '胰岛功能检查', frequency: '每年1次', description: '评估胰岛素分泌功能', nextDate: '2025年8月' }
      ]
    };
    return plans[systemId] || [
      { type: '常规体检', frequency: '每年1次', description: '全面健康检查，及时发现异常', nextDate: '2025年8月' }
    ];
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/chat');
    }
  };

  return (
    <div className="system-detail-page">
      {/* iOS 导航栏 */}
      <div className="ios-nav-bar">
        <button className="ios-back-button" onClick={handleBack}>
          <span className="ios-back-icon">‹</span>
          返回
        </button>
        <div className="ios-nav-title">{system.name}</div>
        <div className="ios-nav-spacer"></div>
      </div>

      {/* iOS 风格内容 */}
      <div className="ios-content">
        {/* 风险标签 */}
        <div className="ios-risk-badge-container">
          <div 
            className="ios-risk-badge"
            style={{ 
              backgroundColor: `${getStatusColor(system.status)}15`, 
              borderColor: getStatusColor(system.status),
              color: getStatusColor(system.status)
            }}
          >
            <span className="ios-risk-icon">{system.icon}</span>
            <span className="ios-risk-text">{system.riskLevel}</span>
          </div>
        </div>

        {/* 既往史列表 */}
        {system.issues.length > 0 && (
          <div className="ios-section">
            <div className="ios-section-header-left">既往史</div>
            <div className="ios-list-card">
              {system.issues.map((issue, index) => {
                const [name, date, summary] = issue.split('|');
                return (
                  <div key={index} className="ios-history-item">
                    <div className="ios-history-header">
                      <div className="ios-issue-dot"></div>
                      <div className="ios-history-name">{name}</div>
                      <div className="ios-history-date">{date}</div>
                    </div>
                    <div className="ios-history-summary">{summary}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}





                 {/* 检查计划 */}
        <div className="ios-section">
          <div className="ios-section-header-left">检查计划</div>
          <div className="ios-list-card">
            {getCheckPlan(system.id).map((plan, index) => (
              <div key={index} className="ios-check-plan-item">
                <div className="ios-check-plan-content">
                  <div className="ios-check-plan-header">
                    <div className="ios-check-plan-type">{plan.type}</div>
                    <div className="ios-check-plan-frequency">{plan.frequency}</div>
                  </div>
                  <div className="ios-check-plan-description">{plan.description}</div>
                  <div className="ios-check-plan-bottom">
                    <div className="ios-check-plan-next">下次检查: {plan.nextDate}</div>
                    <button className="ios-reminder-btn">
                      <span className="ios-reminder-icon">🔔</span>
                      <span>添加提醒</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="ios-actions">
          <button className="ios-primary-button">
            呼叫视频医生
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemDetailPage; 